import React, {useCallback, useEffect, useMemo, useRef} from 'react';
import {v4} from 'uuid';
import {NavigationProps} from "../constants/Props";
import Screen from "../components/Screen";
import Title from "../components/Title";
import Box, {Direction} from "../components/Box";
import TappableAvatar from "../components/TappableAvatar";
import TextInput from "../components/TextInput";
import {IGroupStore} from "../stores/GroupStore";
import {observer} from "mobx-react";
import Modals, {ModalsRefProps} from "../components/Modals";
import {toI18N} from "../utils/I18n";
import {createInvalidImageFunction, createOpenModalFunction, usePrevious} from "../utils/HooksHelper";
import isUrl from "is-url";
import Alert, {AlertRefProps} from "../components/Alert";
import {IGroup} from "../stores/models";
import MultiGroupSelector from "../components/MultiGroupSelector";
import * as Analytics from "../utils/Analytics";

interface Props extends NavigationProps {
    groups: IGroup[],
    store: IGroupStore,
}

function CreateGroupScreen(props: Props) {

    const {
        onBack,
        onNext,
        groups,
        store: {
            avatar,
            name,
            setId,
            setName,
            setExtraGroupIds,
            extraGroupIds,
            setLocalAvatar,
            setURLAvatar,
            removeAvatar,
            canProceedForCreatingGroup
        },
        isActive,
    } = props;

    const prevIsActive = usePrevious(isActive);
    useEffect(() => {
        if (!prevIsActive && isActive) {
            Analytics.logEvent(Analytics.events.land_on_create_group_page);
        }
    });


    // Effects

    useEffect(() => {
        setId(v4());
    }, [setId]);

    // Refs

    const modalsRef = useRef<ModalsRefProps>(null);
    const alertRef = useRef<AlertRefProps>(null);

    // Memo

    const options = useMemo(() => {
        return groups.map(({id, name, selectId}, index) => {
            return { id, selectId, name, value: `${index}`, label: `${name} ${selectId}`};
        });
    }, [groups]);

    // Callbacks

    const selectGroup = useCallback(
        (indexes) => {
            if (!indexes) {
                setExtraGroupIds([]);
                return;
            }
            setExtraGroupIds(indexes.map((v: number) => groups[v].id));

        },
        [groups, setExtraGroupIds],
    );

    const showActionsModal = useCallback(
        createOpenModalFunction(modalsRef, setLocalAvatar, setURLAvatar, removeAvatar, avatar),
        [setLocalAvatar, setURLAvatar, removeAvatar, avatar],
    );

    const handleOnError = useCallback(
        createInvalidImageFunction(alertRef, removeAvatar),
        [removeAvatar],
    );

    const value:any = extraGroupIds.map((groupId) => {
        return options.find(({id}) => id === groupId);
    });

    return (
        <Screen
            title={"create_group_screen_header_title"}
            subtitle={"create_group_screen_header_subtitle"}
            onBack={onBack}
            onNext={onNext}
            disabledNext={!canProceedForCreatingGroup}>

            <Alert ref={alertRef}/>

            <Modals
                id={'create-group-screen'}
                modalTextInputValidation={isUrl}
                ref={modalsRef}/>

            <Box center topMargin={116}>
                <Title shrink title={toI18N("create_group_title")}
                       subtitle={toI18N("create_group_subtitle")}/>
            </Box>

            <Box grow top sideMargin direction={Direction.Column} topMargin={50}>

                <Box direction={Direction.Column} fullWidth center>
                    <TappableAvatar onError={handleOnError} src={avatar ? avatar.path : undefined}
                                    onClick={showActionsModal}
                                    onLoad={avatar ? avatar.setIsLoaded : undefined}
                                    hint={toI18N("create_group_avatar_hint")}/>
                </Box>

                <Box direction={Direction.Clear} fullWidth topMargin={35}>
                    <Box direction={Direction.Column}>
                        <TextInput placeholder={toI18N("create_group_input_placeholder")}
                                   id={"group-name-create-group-screen"}
                                   maxLength={100}
                                   autoComplete={"new-password"} // Trick to make chrome not to auto-fill this field when selecting email
                                   value={name}
                                   onChange={setName}/>
                    </Box>
                    <Box direction={Direction.Column} topMargin={14}>
                        {!!options.length &&  <MultiGroupSelector
                            id={'create-group-multi-group-selector'}
                            placeholder={toI18N("create_group_select_placeholder")}
                            label={toI18N("create_group_select_groups_label")}
                            hint={toI18N("create_group_select_hint")}
                            onSelectGroup={selectGroup}
                            options={options}
                            value={value}
                        />}
                    </Box>

                </Box>

            </Box>

        </Screen>
    );
}

export default observer(CreateGroupScreen);
